/**
 * Per-customer product eligibility for the Campaigns drawer Recos + Refer
 * (SCRUM-207), ported from Ryan's `customerEligibility`. Wraps the shared
 * rule-evaluator (SCRUM-68) so the rule semantics stay in one place.
 */
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';
import { evaluateRule } from '@/features/product-scan/rule-evaluator';

export interface RuleResult {
  label: string;
  met: boolean;
  required: boolean;
}

export interface Eligibility {
  rules: RuleResult[];
  met: number;
  total: number;
  /** True when every required (forced) rule is met, regardless of threshold. */
  forcedAllMet: boolean;
}

/** Evaluates every rule of a product against a customer. */
export function customerEligibility(customer: Customer, product: Product): Eligibility {
  const rules: RuleResult[] = product.rules.map((r) => ({
    label: r.label,
    met: evaluateRule(r.key, customer, r.threshold, r.expects),
    required: r.required ?? false,
  }));
  const met = rules.filter((r) => r.met).length;
  const forcedAllMet = rules.every((r) => !r.required || r.met);
  return { rules, met, total: rules.length, forcedAllMet };
}

export interface RankedProduct {
  product: Product;
  eligibility: Eligibility;
  /** Integer percent: round(met / total * 100). */
  score: number;
}

/**
 * Applies a global product-priority order (SCRUM-46) to an already-ranked list.
 * STABLE: items whose id is in `order` sort by their position in `order`;
 * everything else keeps its incoming relative order (which is the caller's
 * score sort) and sorts AFTER the prioritized items. Total + tolerant —
 * duplicate ids in `order` use their first occurrence; ids in `order` that
 * match no item are ignored; an empty/undefined `order` is the identity
 * transform (strictly additive — RT-E, RT-1).
 *
 * Display-level ONLY: this reorders an existing list; it never touches
 * eligibility, `met`, or `total`.
 */
export function applyPriorityOrder<T>(
  items: readonly T[],
  order: readonly string[] | undefined,
  getId: (item: T) => string,
): T[] {
  if (!order || order.length === 0) {
    return [...items];
  }
  // First-occurrence index per id (dedupe deterministically).
  const rank = new Map<string, number>();
  order.forEach((id, i) => {
    if (!rank.has(id)) rank.set(id, i);
  });
  const NOT_PRIORITIZED = Number.MAX_SAFE_INTEGER;
  // Stable sort: decorate with original index so equal keys preserve input order.
  return items
    .map((item, i) => ({ item, i, r: rank.get(getId(item)) ?? NOT_PRIORITIZED }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((d) => d.item);
}

/**
 * Ranks products this customer is eligible for (all required rules met),
 * highest score first. Used by the drawer Recos (top-3) and Refer popover.
 *
 * SCRUM-46: when a global product-priority `order` is supplied, the eligible
 * set is re-sorted by that order (score is the tiebreak / fallback for
 * unlisted products). Omitting `order` preserves the pure score sort — the
 * feature is strictly additive.
 */
export function rankEligibleProducts(
  customer: Customer,
  products: Product[],
  order?: readonly string[],
): RankedProduct[] {
  const ranked = products
    .map((product) => {
      const eligibility = customerEligibility(customer, product);
      return {
        product,
        eligibility,
        score: eligibility.total ? Math.round((eligibility.met / eligibility.total) * 100) : 0,
      };
    })
    .filter((r) => r.eligibility.forcedAllMet)
    .sort((a, b) => b.score - a.score);
  return applyPriorityOrder(ranked, order, (r) => r.product.id);
}

/**
 * The product-first auto-targeting predicate (SCRUM-500): customers eligible
 * for a product = every REQUIRED rule met (forcedAllMet), the same semantics
 * as the drawer Recos / Refer popover ranking above. SINGLE SOURCE: the
 * campaign wizard's auto-computed target set and its product-card eligibility
 * counts both call this — the wizard can never disagree with the drawer or
 * the product scan about who qualifies.
 */
export function eligibleTargets(customers: Customer[], product: Product): Customer[] {
  return customers.filter((c) => rankEligibleProducts(c, [product]).length > 0);
}
